import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from '../../../config/axios';
import { Button } from '../../components/Atoms';
import AuthShell, { AuthLabel, AuthError, AuthSuccess, authInputStyle } from './AuthShell';

const PASSWORD_RULES = [
  { test: p => p.length >= 8, label: 'Min. 8 znaków' },
  { test: p => /[A-ZĄĆĘŁŃÓŚŹŻ]/.test(p), label: 'Wielka litera' },
  { test: p => /[a-ząćęłńóśźż]/.test(p), label: 'Mała litera' },
  { test: p => /\d/.test(p), label: 'Cyfra' },
  { test: p => /[!@#$%^&*(),.?":{}|<>]/.test(p), label: 'Znak specjalny' },
];

function Checklist({ password }) {
  return (
    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {PASSWORD_RULES.map((rule, i) => {
        const ok = rule.test(password);
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 11,
            color: ok ? 'var(--t-sage)' : 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            transition: 'color 200ms ease',
          }}>
            <span style={{
              display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
              border: `1.5px solid ${ok ? 'var(--t-sage)' : 'var(--t-line-2)'}`,
              background: ok ? 'var(--t-sage)' : 'transparent',
              flexShrink: 0, position: 'relative',
            }}>
              {ok && (
                <svg width="8" height="8" viewBox="0 0 14 14" fill="none"
                     style={{ position: 'absolute', top: 1, left: 1 }}>
                  <path d="M3 7.5l3 3 6-6.5" stroke="var(--t-bg)" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            {rule.label}
          </div>
        );
      })}
    </div>
  );
}

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null|true|false

  // Sprawdź ważność tokenu przy wejściu
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    axios.get(`/api/auth/verify-reset-token/${token}`)
      .then(() => setTokenValid(true))
      .catch(() => setTokenValid(false));
  }, [token]);

  const allRulesPassed = PASSWORD_RULES.every(r => r.test(password));
  const passwordsMatch = password && password === confirm;
  const canSubmit = allRulesPassed && passwordsMatch && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setSuccess('Hasło zostało zmienione. Przekierowujemy do logowania…');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się zresetować hasła. Spróbuj ponownie.');
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <AuthShell
        eyebrow="Sprawdzam token"
        title="Zaraz, zaraz…"
        subtitle="Weryfikujemy, czy link jest jeszcze ważny."
      >
        <div style={{
          padding: '20px 0', textAlign: 'center',
          fontFamily: 'var(--t-mono)', fontSize: 11,
          letterSpacing: '.18em', textTransform: 'uppercase',
          color: 'var(--t-text-mute)',
        }}>Wczytywanie…</div>
      </AuthShell>
    );
  }

  if (tokenValid === false) {
    return (
      <AuthShell
        eyebrow="Link wygasł"
        title={<>Niestety <span style={{ fontStyle: 'italic', color: 'var(--t-rust)' }}>za późno</span>.</>}
        subtitle="Link do resetu hasła jest ważny godzinę po wysłaniu. Wyślij sobie nowy."
        footer={
          <div style={{ fontSize: 13, color: 'var(--t-text-dim)' }}>
            <Link to="/forgot-password" style={{
              color: 'var(--t-sage)', textDecoration: 'none', fontWeight: 500,
            }}>Wyślij nowy link</Link>
          </div>
        }
      >
        <AuthError>Token nieprawidłowy lub wygasł.</AuthError>
      </AuthShell>
    );
  }

  if (success) {
    return (
      <AuthShell
        eyebrow="Gotowe"
        title={<>Hasło <span style={{ fontStyle: 'italic', color: 'var(--t-sage)' }}>zmienione</span>.</>}
        subtitle="Za chwilę przekierujemy Cię do logowania."
      >
        <AuthSuccess>{success}</AuthSuccess>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Nowe hasło"
      title={<>Ustaw <span style={{ fontStyle: 'italic', color: 'var(--t-sage)' }}>nowe</span> hasło.</>}
      subtitle="Wymyśl coś, czego jeszcze nie używałeś. Im dłuższe, tym lepsze."
      footer={
        <div style={{ fontSize: 13, color: 'var(--t-text-dim)' }}>
          <Link to="/login" style={{
            color: 'var(--t-sage)', textDecoration: 'none', fontWeight: 500,
          }}>← Wróć do logowania</Link>
        </div>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <AuthError>{error}</AuthError>

        <div style={{ marginBottom: 16 }}>
          <AuthLabel htmlFor="password">Nowe hasło</AuthLabel>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            style={authInputStyle}
            autoFocus
          />
          {password && <Checklist password={password} />}
        </div>

        <div style={{ marginBottom: 22 }}>
          <AuthLabel htmlFor="confirm">Powtórz nowe hasło</AuthLabel>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder="••••••••"
            style={{
              ...authInputStyle,
              borderColor: confirm && !passwordsMatch ? 'var(--t-rust)' : 'var(--t-line-2)',
            }}
          />
          {confirm && !passwordsMatch && (
            <div style={{
              marginTop: 6, fontSize: 11, color: 'var(--t-rust)',
              fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            }}>Hasła się różnią</div>
          )}
        </div>

        <Button
          type="submit"
          full
          variant={canSubmit ? 'primary' : 'ghost'}
        >{loading ? 'Zmieniam…' : 'Ustaw nowe hasło'}</Button>
      </form>
    </AuthShell>
  );
}
